/**
 * wakeWord.js — Hustle wake-phrase listener.
 *
 * Strategy
 * --------
 * 1. Pull the current wake-phrase list from `/api/voice/wake-phrases`
 *    (merged with this user's overrides).
 * 2. If Picovoice Porcupine is available (web SDK or native Capacitor
 *    plugin), use it for offline, low-power wake-word detection.
 * 3. Otherwise fall back to the Web Speech API in "continuous" mode
 *    while the app tab is active. (Background detection on iOS isn't
 *    possible without violating App Review; on Android we recommend
 *    the Capacitor Porcupine plugin for a true background service.)
 *
 * Callback contract
 * -----------------
 * The caller passes `onWake({ slug, phrase, deepLink, action, ... })`.
 * The listener fires it whenever any wake phrase is matched.
 */

import { API_BASE } from "./api";

const STORAGE_KEY = "hp_wake_state_v1";

let _state = {
  enabled: false,
  engine: null,         // "porcupine" | "webspeech" | null
  recognition: null,    // SpeechRecognition instance
  porcupine: null,      // Porcupine worker
  phrases: [],          // [{slug, phrase, deep_link, action, ...}, ...]
  onWake: null,         // user callback
  lastWakeAt: 0,
};

/** Fetch the current phrase list (admin defaults + user overrides). */
export async function fetchWakePhrases() {
  try {
    const r = await fetch(`${API_BASE}/voice/wake-phrases`,
                          { credentials: "include" });
    const j = await r.json();
    return Array.isArray(j.items) ? j.items : [];
  } catch (e) {
    console.warn("[wakeWord] fetch failed", e);
    return [];
  }
}

function _norm(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "").trim();
}

// ─── Engine note (Feb 2026) ───────────────────────────────────────
// We initially planned to use Picovoice Porcupine for true background
// "Hey Hustle" detection. Picovoice killed their free tier in mid-2026
// and openWakeWord's hosted training also moved to credit-paid. We
// pivoted to using the device's NATIVE assistant on each platform
// (Apple Siri Shortcuts, Google Assistant App Actions, Samsung Bixby)
// — all free, zero subscription, App Store + Play Store approved.
//
// What this file still does:
//   • Foreground listening via Web Speech API (works in Chrome/Edge
//     while the PWA tab is active — useful for desktop and as a
//     fallback on web).
//   • The Picovoice import is kept as a no-op fallback in case the
//     SDK is ever installed later, but no AccessKey is required.

function _matchPhrase(transcript, phrases) {
  const t = _norm(transcript);
  if (!t || phrases.length === 0) return null;
  // Exact > contains > word-overlap
  let best = null;
  let bestScore = 0;
  for (const p of phrases) {
    const pn = _norm(p.phrase);
    if (!pn) continue;
    let score = 0;
    if (t === pn) score = 100;
    else if (t.includes(pn)) score = 85;
    else {
      const tw = new Set(t.split(/\s+/));
      const pw = pn.split(/\s+/);
      const hits = pw.filter((w) => tw.has(w)).length;
      if (pw.length) score = Math.round((hits / pw.length) * 70);
    }
    if (score > bestScore) { bestScore = score; best = p; }
  }
  return bestScore >= 70 ? { ...best, score: bestScore } : null;
}

/** Web Speech API fallback — works in any browser (Chrome best). */
function _startWebSpeech() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const rec = new SR();
  rec.continuous = true;
  rec.interimResults = false;
  rec.lang = "en-AU";

  rec.onresult = (ev) => {
    const last = ev.results[ev.results.length - 1];
    if (!last) return;
    const transcript = last[0]?.transcript || "";
    const match = _matchPhrase(transcript, _state.phrases);
    if (!match) return;
    // Debounce: 1 wake every 2 seconds
    const now = Date.now();
    if (now - _state.lastWakeAt < 2000) return;
    _state.lastWakeAt = now;
    try { _state.onWake?.(match, transcript); }
    catch (e) { console.warn("[wakeWord] handler error", e); }
  };

  rec.onerror = (e) => {
    console.warn("[wakeWord] recognition error", e.error || e);
    // Auto-restart on network/no-speech errors
    if (_state.enabled && e.error !== "not-allowed") {
      setTimeout(() => { try { rec.start(); } catch {} }, 1000);
    }
  };

  rec.onend = () => {
    if (_state.enabled) {
      setTimeout(() => { try { rec.start(); } catch {} }, 250);
    }
  };

  try { rec.start(); } catch (e) {
    console.warn("[wakeWord] start failed", e);
    return null;
  }
  return rec;
}

/**
 * Try to start the Picovoice Porcupine engine (web SDK).
 * Returns the engine handle if successful, else null.
 *
 * This is best-effort — if @picovoice/porcupine-web isn't installed
 * or the access key isn't set, we fall through to Web Speech.
 *
 * To enable:
 *   1. yarn add @picovoice/porcupine-web @picovoice/web-voice-processor
 *   2. Set REACT_APP_PICOVOICE_KEY in frontend/.env
 *   3. Upload your trained .ppn files to /public/porcupine/
 */
async function _startPorcupine() {
  const accessKey = process.env.REACT_APP_PICOVOICE_KEY;
  if (!accessKey) return null;

  let PorcupineWorker, BuiltInKeyword;
  try {
    const mod = await import("@picovoice/porcupine-web");
    PorcupineWorker = mod.PorcupineWorker;
    BuiltInKeyword = mod.BuiltInKeyword;
  } catch {
    // Library not installed yet — gracefully fall back
    return null;
  }

  // We need at least one .ppn file or built-in keyword per phrase.
  // For initial setup we use BuiltInKeyword.Computer as a sanity test
  // until the user uploads their custom-trained .ppn files.
  const keywords = _state.phrases.map((p, i) => ({
    label: p.slug,
    publicPath: `/porcupine/${p.slug}.ppn`,   // user uploads these
    sensitivity: 0.6,
  })).filter(k => k.publicPath);

  try {
    const worker = await PorcupineWorker.create(
      accessKey,
      keywords,
      (detection) => {
        const slug = detection.label;
        const match = _state.phrases.find((p) => p.slug === slug);
        if (!match) return;
        const now = Date.now();
        if (now - _state.lastWakeAt < 1500) return;
        _state.lastWakeAt = now;
        _state.onWake?.(match, `(porcupine:${slug})`);
      },
    );
    const { WebVoiceProcessor } = await import("@picovoice/web-voice-processor");
    await WebVoiceProcessor.subscribe(worker);
    return worker;
  } catch (e) {
    console.warn("[wakeWord] porcupine init failed", e);
    return null;
  }
}

/** Start listening. Resolves once an engine is running. */
export async function startWakeWord({ onWake } = {}) {
  if (_state.enabled) return _state.engine;
  // Always also fire a window event so the global handler can route
  _state.onWake = (match, transcript) => {
    try {
      window.dispatchEvent(new CustomEvent("hustle:wake", {
        detail: { ...match, transcript },
      }));
    } catch {}
    try { onWake?.(match, transcript); } catch (e) { console.warn(e); }
  };
  _state.phrases = await fetchWakePhrases();
  if (_state.phrases.length === 0) {
    console.warn("[wakeWord] no phrases loaded — abort");
    return null;
  }
  _state.enabled = true;
  // Prefer Porcupine (offline, no network) → fall back to Web Speech
  _state.porcupine = await _startPorcupine();
  if (_state.porcupine) {
    _state.engine = "porcupine";
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ engine: "porcupine" })); } catch {}
    return _state.engine;
  }
  _state.recognition = _startWebSpeech();
  if (_state.recognition) {
    _state.engine = "webspeech";
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ engine: "webspeech" })); } catch {}
    return _state.engine;
  }
  _state.enabled = false;
  return null;
}

export async function stopWakeWord() {
  _state.enabled = false;
  if (_state.recognition) {
    try { _state.recognition.stop(); } catch {}
    _state.recognition = null;
  }
  if (_state.porcupine) {
    try { _state.porcupine.terminate(); } catch {}
    _state.porcupine = null;
  }
  _state.engine = null;
}

export async function refreshWakePhrases() {
  _state.phrases = await fetchWakePhrases();
  return _state.phrases.length;
}

export function getWakeWordState() {
  return {
    enabled: _state.enabled,
    engine: _state.engine,
    phraseCount: _state.phrases.length,
    phrases: _state.phrases,
  };
}
